import { useRef } from 'react';

const LOGO_URL = 'https://i.ibb.co/wFQFKlhN/Design-sem-nome.png';

const contentLines = Array.from({ length: 28 });

export default function PapelTimbrado() {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-[#f5f0e8] flex flex-col items-center py-10 px-4">

      {/* Toolbar — hidden on print */}
      <div className="no-print w-full max-w-[900px] flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[#0f2651] font-serif text-2xl font-bold">Papel Timbrado</h1>
          <p className="text-[#0f2651]/60 text-sm mt-0.5">RCP Inteligência Contábil &amp; Tributária</p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/"
            className="inline-flex items-center gap-2 border border-[#0f2651]/30 text-[#0f2651] text-sm font-medium px-4 py-2 rounded-md hover:bg-[#0f2651]/5 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm" />
            </div>
            Voltar ao site
          </a>
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 bg-[#c9a84c] hover:bg-[#b8943d] text-[#1a2535] text-sm font-semibold px-5 py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-printer-line text-sm" />
            </div>
            Imprimir / Salvar PDF
          </button>
        </div>
      </div>

      {/* Hint — hidden on print */}
      <div className="no-print w-full max-w-[900px] mb-5 bg-[#0f2651]/5 border border-[#0f2651]/15 rounded-lg px-4 py-3 flex items-start gap-3">
        <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
          <i className="ri-information-line text-[#c9a84c] text-base" />
        </div>
        <p className="text-[#0f2651]/70 text-sm leading-relaxed">
          Clique em <strong className="text-[#0f2651]">Imprimir / Salvar PDF</strong> para exportar. Na janela de impressão, selecione <strong className="text-[#0f2651]">"Salvar como PDF"</strong> na impressora para gerar o arquivo. Recomendamos usar o formato <strong className="text-[#0f2651]">A4</strong> sem margens adicionais.
        </p>
      </div>

      {/* A4 Sheet */}
      <div
        ref={printRef}
        id="papel-timbrado-sheet"
        className="bg-white w-full max-w-[794px] min-h-[1123px] relative flex flex-col shadow-lg"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        {/* Gold top bar */}
        <div className="w-full h-[6px] bg-gradient-to-r from-[#c9a84c] via-[#e8c96a] to-[#c9a84c]" />

        {/* Header */}
        <div className="bg-[#0f2651] px-10 py-6 flex items-center justify-between">
          {/* Logo + brand */}
          <div className="flex items-center gap-4">
            <img
              src={LOGO_URL}
              alt="Escudo RCP"
              className="w-14 h-14 object-contain"
            />
            <div className="flex flex-col leading-tight">
              <span className="font-serif font-bold text-white text-2xl tracking-wide">RCP</span>
              <span className="text-[#c9a84c] text-[10px] font-semibold tracking-[0.2em] uppercase">
                Inteligência Contábil &amp; Tributária
              </span>
              <span className="text-white/40 text-[9px] tracking-widest uppercase mt-0.5 italic">
                A contabilidade com o olhar de quem audita
              </span>
              <span className="text-[#c9a84c]/50 text-[9px] tracking-widest uppercase mt-0.5">
                CRC RS 011307/O-2
              </span>
            </div>
          </div>

          {/* Contact info */}
          <div className="flex flex-col items-end gap-1.5 text-right">
            <div className="flex items-center gap-1.5">
              <span className="text-white/60 text-[10px]">contato@rcpinteligencia.com.br</span>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-mail-line text-[#c9a84c] text-[10px]" />
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-white/60 text-[10px]">(51) 99100-3410</span>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-[#c9a84c] text-[10px]" />
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-white/60 text-[10px]">Porto Alegre, RS — Brasil</span>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-map-pin-2-line text-[#c9a84c] text-[10px]" />
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-white/60 text-[10px]">rcpinteligencia.com.br</span>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-global-line text-[#c9a84c] text-[10px]" />
              </div>
            </div>
          </div>
        </div>

        {/* Gold divider */}
        <div className="w-full h-[2px] bg-gradient-to-r from-[#c9a84c]/80 via-[#c9a84c]/30 to-transparent" />

        {/* Document meta row */}
        <div className="px-10 py-4 flex items-center justify-between border-b border-[#0f2651]/8">
          <div className="flex items-center gap-6">
            <div>
              <span className="text-[#0f2651]/40 text-[9px] uppercase tracking-widest block">Documento</span>
              <span className="text-[#0f2651] text-xs font-medium">___________________________</span>
            </div>
            <div>
              <span className="text-[#0f2651]/40 text-[9px] uppercase tracking-widest block">Referência</span>
              <span className="text-[#0f2651] text-xs font-medium">___________________________</span>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <span className="text-[#0f2651]/40 text-[9px] uppercase tracking-widest block">Data</span>
              <span className="text-[#0f2651] text-xs font-medium">_____ / _____ / _________</span>
            </div>
            <div className="text-right">
              <span className="text-[#0f2651]/40 text-[9px] uppercase tracking-widest block">Página</span>
              <span className="text-[#0f2651] text-xs font-medium">1 / 1</span>
            </div>
          </div>
        </div>

        {/* Body — writing area */}
        <div className="flex-1 px-10 pt-8 pb-4 flex flex-col">

          {/* Destination block */}
          <div className="mb-6">
            <span className="text-[#0f2651]/40 text-[9px] uppercase tracking-widest block mb-1">Destinatário / Assunto</span>
            <div className="border-b border-[#0f2651]/15 pb-1 mb-2" />
            <div className="border-b border-[#0f2651]/15 pb-1" />
          </div>

          {/* Writing lines */}
          <div className="flex-1 flex flex-col gap-0">
            {contentLines.map((_, i) => (
              <div
                key={i}
                className="border-b border-[#0f2651]/8"
                style={{ height: '28px' }}
              />
            ))}
          </div>

          {/* Signature block */}
          <div className="mt-8 flex items-end justify-between">
            <div className="flex flex-col items-center gap-1">
              <div className="w-48 border-b border-[#0f2651]/30" />
              <span className="text-[#0f2651]/50 text-[9px] uppercase tracking-widest">Assinatura</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-48 border-b border-[#0f2651]/30" />
              <span className="text-[#0f2651]/50 text-[9px] uppercase tracking-widest">Cargo / Função</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <div className="w-36 border-b border-[#0f2651]/30" />
              <span className="text-[#0f2651]/50 text-[9px] uppercase tracking-widest">Data</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto">
          {/* Gold divider */}
          <div className="w-full h-[1px] bg-gradient-to-r from-[#c9a84c]/60 via-[#c9a84c]/20 to-transparent mx-10" style={{ width: 'calc(100% - 80px)', marginLeft: '40px' }} />

          <div className="bg-[#0f2651]/4 px-10 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img
                src={LOGO_URL}
                alt="RCP"
                className="w-6 h-6 object-contain opacity-60"
              />
              <span className="text-[#0f2651]/50 text-[9px] font-semibold tracking-widest uppercase">
                RCP Inteligência Contábil &amp; Tributária
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[#0f2651]/40 text-[9px]">CRC RS 011307/O-2</span>
              <span className="text-[#c9a84c]/60 text-[9px]">·</span>
              <span className="text-[#0f2651]/40 text-[9px]">@inteligenciarcp</span>
              <span className="text-[#c9a84c]/60 text-[9px]">·</span>
              <span className="text-[#0f2651]/40 text-[9px]">rcpinteligencia.com.br</span>
            </div>
          </div>

          {/* Bottom gold bar */}
          <div className="w-full h-[4px] bg-gradient-to-r from-[#c9a84c] via-[#e8c96a] to-[#c9a84c]" />
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; margin: 0 !important; padding: 0 !important; }
          #papel-timbrado-sheet {
            box-shadow: none !important;
            width: 100% !important;
            max-width: 100% !important;
            min-height: 100vh !important;
            page-break-after: avoid;
          }
          @page {
            size: A4;
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
}
